import React from 'react';
import { Calendar, MapPin, Video } from 'lucide-react';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import '../styles/TicketDetail.css'; // Sidebar-/Meta-Klassen des Inhalts

const CalendarEventDetail = ({ event, onClose }) => {
  const { t, getLocale } = useI18n();

  if (!event) return null;

  const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  const parseUtc = (dateTime) => {
    try {
      const utc = String(dateTime || '').endsWith('Z') ? dateTime : `${dateTime}Z`;
      return new Date(utc);
    } catch (_) {
      return new Date(dateTime);
    }
  };

  const start = parseUtc(event?.start?.dateTime);
  const end = parseUtc(event?.end?.dateTime);

  const formatDate = (d) => {
    try {
      return d.toLocaleDateString(getLocale(), {
        weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: getUserTimeZone()
      });
    } catch (_) { return ''; }
  };

  const formatTime = (d) => {
    try {
      return d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', timeZone: getUserTimeZone() });
    } catch (_) { return ''; }
  };

  const getShowAsLabel = (showAs) => {
    switch (showAs) {
      case 'busy': return t('eventDetail.busy', 'Beschäftigt');
      case 'tentative': return t('eventDetail.tentative', 'Vorläufig');
      case 'free': return t('eventDetail.free', 'Frei');
      case 'oof': return t('eventDetail.oof', 'Abwesend');
      default: return t('eventDetail.appointment', 'Termin');
    }
  };

  // Teams-Meeting erkennen (nur dann Button/Description anzeigen)
  const provider = String(event?.onlineMeetingProvider || '').toLowerCase();
  const joinUrlCandidate = event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null;
  const isTeamsMeeting = Boolean(
    (event?.isOnlineMeeting && provider.includes('teams')) ||
    (joinUrlCandidate && /https?:\/\/(.*\.)?teams\.microsoft\.com/i.test(joinUrlCandidate))
  );
  const onlineMeetingUrl = isTeamsMeeting ? (event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null) : null;
  const locationName = event?.location?.displayName || '';
  const subject = event?.subject || t('eventDetail.noSubject', '(Kein Betreff)');
  const organizer = event?.organizer?.emailAddress?.name || event?.organizer?.emailAddress?.address || null;
  const attendees = Array.isArray(event?.attendees) ? event.attendees : [];

  const stripHtml = (html) => {
    try {
      return String(html || '').replace(/<[^>]+>/g, '').trim();
    } catch (_) { return ''; }
  };
  const bodyText = stripHtml(event?.body?.content);

  // #573: auf ui/Modal umgestellt. Vorher lieh sich der Dialog die Huelle von
  // TicketDetail (.ticket-detail-flex-wrap ohne Breite): Seit dort am 20.08.
  // (W0-3) die Mindestbreite fiel, schrumpfte er auf seinen Inhalt, und
  // overflow:hidden schnitt den Betreff ab.
  return (
    <Modal
      open
      onClose={() => onClose && onClose()}
      title={t('eventDetail.header', 'Termin')}
      icon={<Calendar size={18} />}
      maxWidth="760px"
      closeLabel={t('common.close', 'Schließen')}
      className="calendar-event-detail"
    >
      <div className="ticket-info">
        <div className="ticket-main-content">
          <div className="ticket-meta-sidebar" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="meta-item">
              <span className="meta-label">{t('eventDetail.status', 'Status')}</span>
              <span className="meta-value">{getShowAsLabel(event?.showAs)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{t('eventDetail.date', 'Datum')}</span>
              <span className="meta-value">{formatDate(start)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{t('eventDetail.period', 'Zeitraum')}</span>
              <span className="meta-value">{formatTime(start)} - {formatTime(end)}</span>
            </div>
            {locationName && (
              <div className="meta-item">
                <span className="meta-label">{t('eventDetail.location', 'Ort')}</span>
                <span className="meta-value"><MapPin size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />{locationName}</span>
              </div>
            )}
            {organizer && (
              <div className="meta-item">
                <span className="meta-label">{t('eventDetail.organizer', 'Organisator')}</span>
                <span className="meta-value">{organizer}</span>
              </div>
            )}
            {attendees.length > 0 && (
              <div className="meta-item">
                <span className="meta-label">{t('eventDetail.attendees', 'Teilnehmer')}</span>
                <span className="meta-value">{attendees.map(a => a.emailAddress?.name || a.emailAddress?.address).filter(Boolean).slice(0, 5).join(', ')}{attendees.length > 5 ? ` +${attendees.length - 5}` : ''}</span>
              </div>
            )}
          </div>

          <div className="ticket-content-area">
            <div className="ticket-subject" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} />
              <h3 style={{ overflowWrap: 'anywhere', margin: 0 }}>{subject}</h3>
            </div>

            {isTeamsMeeting && onlineMeetingUrl && (
              <div className="description-content" style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <a href={onlineMeetingUrl} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Video size={16} /> {t('eventDetail.joinMeeting', 'Besprechung beitreten')}
                </a>
              </div>
            )}

            {isTeamsMeeting && bodyText && (
              <div className="description-content" style={{ marginTop: 12 }}>
                {bodyText}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CalendarEventDetail;


